// src/pages/surveys/SurveyResults.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

const s = {
  page: {},
  header: { marginBottom: 28 },
  titleRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 6 },
  title: { fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--ka-navy)' },
  backBtn: {
    padding: '8px 16px', background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 7, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-body)',
    color: 'var(--ka-gray-600)', fontWeight: 600,
  },
  statusPill: {
    padding: '4px 12px', borderRadius: 20, fontSize: 11,
    fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 },
  statCard: {
    background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 12, padding: '18px 20px', boxShadow: 'var(--shadow-sm)',
  },
  statLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ka-gray-400)', marginBottom: 4 },
  statVal: { fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ka-navy)' },
  statSub: { fontSize: 12, color: 'var(--ka-gray-400)', marginTop: 2 },
  tabs: { display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--ka-gray-200)', paddingBottom: -2 },
  tab: {
    padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    marginBottom: -2, color: 'var(--ka-gray-400)', fontFamily: 'var(--font-body)',
  },
  tabActive: { color: 'var(--ka-navy)', borderBottomColor: 'var(--ka-navy)' },
  summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  qCard: {
    background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 12, padding: '20px 24px', boxShadow: 'var(--shadow-sm)',
  },
  qCardFull: { gridColumn: '1 / -1' },
  qLabel: { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--ka-navy)', marginBottom: 4 },
  qType: { fontSize: 11, color: 'var(--ka-gold)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 },
  barRow: { marginBottom: 8 },
  barLabel: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ka-gray-800)', marginBottom: 4 },
  barOuter: { height: 8, background: 'var(--ka-gray-100)', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 4, background: 'var(--ka-navy)', transition: 'width 0.5s' },
  textResponses: { maxHeight: 200, overflowY: 'auto' },
  textItem: { padding: '8px 0', borderBottom: '1px solid var(--ka-gray-100)', fontSize: 13, color: 'var(--ka-gray-800)', lineHeight: 1.5 },
  ratingAvg: { fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 700, color: 'var(--ka-navy)', textAlign: 'center', padding: '16px 0' },
  ratingMax: { fontSize: 14, color: 'var(--ka-gray-400)', textAlign: 'center', marginTop: -10 },
  // Individual responses tab
  responseCard: {
    background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 10, marginBottom: 12, overflow: 'hidden',
  },
  responseHead: {
    padding: '12px 20px', background: 'var(--ka-gray-50)',
    borderBottom: '1px solid var(--ka-gray-200)',
    display: 'flex', alignItems: 'center', gap: 12, fontSize: 13,
  },
  responseBody: { padding: '16px 20px' },
  responseItem: { marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--ka-gray-100)' },
  responseQ: { fontSize: 12, fontWeight: 700, color: 'var(--ka-gray-400)', letterSpacing: '0.04em', marginBottom: 4 },
  responseA: { fontSize: 14, color: 'var(--ka-navy)', lineHeight: 1.5 },
  noData: { textAlign: 'center', padding: '60px 20px', color: 'var(--ka-gray-400)' },
  shareBar: {
    background: 'var(--ka-white)', border: '1px solid var(--ka-gray-200)',
    borderRadius: 10, padding: '14px 20px', marginBottom: 24,
    display: 'flex', alignItems: 'center', gap: 12,
  },
  copyBtn: {
    padding: '7px 14px', background: 'var(--ka-gold)', color: 'var(--ka-navy)',
    border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
  },
};

// ─── CSV export ────────────────────────────────────────────────────────────────
function downloadCSV(survey, responses) {
  const questions = (survey.questions || []).filter(q => q.type !== 'section');

  function escape(val) {
    const s = String(val ?? '');
    return (s.includes(',') || s.includes('"') || s.includes('\n'))
      ? `"${s.replace(/"/g, '""')}"` : s;
  }

  const headers = ['Respondent', 'Email', 'Submitted At', ...questions.map(q => q.label || q.id)];
  const rows = responses.map(r => [
    escape(r.respondentName || 'Anonymous'),
    escape(r.respondentEmail || ''),
    escape(r.submittedAt?.toDate?.()?.toLocaleString('en-US') || ''),
    ...questions.map(q => {
      const ans = r.answers?.[q.id];
      if (ans === undefined || ans === null) return '';
      if (q.type === 'file_upload') {
        const files = Array.isArray(ans) ? ans : [ans];
        return escape(files.map(f => `${f.name}: ${f.url}`).join(' | '));
      }
      return escape(Array.isArray(ans) ? ans.join('; ') : String(ans));
    }),
  ]);

  const csv = [headers.map(escape).join(','), ...rows.map(r => r.join(','))].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${survey.title || 'survey-results'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Word export helpers ───────────────────────────────────────────────────────
function wordBlob(html) {
  // Word-compatible HTML wrapped in the Office namespace so Word opens it natively
  const full = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8">
<style>
  body { font-family: Calibri, sans-serif; font-size: 11pt; color: #1a2744; margin: 2cm; }
  h1 { font-size: 18pt; color: #1a2744; margin-bottom: 4pt; }
  h2 { font-size: 13pt; color: #1a2744; margin: 16pt 0 4pt; border-bottom: 1pt solid #e2e8f0; padding-bottom: 4pt; }
  h3 { font-size: 11pt; color: #1a2744; margin: 12pt 0 2pt; }
  p  { margin: 2pt 0; font-size: 10pt; color: #334155; }
  .meta { font-size: 9pt; color: #94a3b8; }
  .label { font-size: 9pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.05em; color: #f59e0b; }
  .answer { font-size: 10pt; color: #1a2744; margin: 2pt 0 10pt; }
  .opt-row { font-size: 10pt; margin: 2pt 0; }
  .divider { border: none; border-top: 1pt solid #e2e8f0; margin: 12pt 0; }
  .respondent-block { page-break-inside: avoid; margin-bottom: 20pt; }
</style>
</head><body>${html}</body></html>`;
  return new Blob([full], { type: 'application/msword' });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── 1. Summary Word export (aggregated, same structure as PDF) ──────────────
function downloadWord(survey, responses) {
  const questions = (survey.questions || []).filter(q => q.type !== 'section');
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  function getStats(q) {
    const answers = responses.map(r => r.answers?.[q.id]).filter(a => a !== undefined && a !== null && a !== '');
    if (!answers.length) return null;
    if (q.type === 'multiple_choice' || q.type === 'dropdown') {
      const counts = {}; answers.forEach(a => { counts[a] = (counts[a] || 0) + 1; });
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'checkboxes') {
      const counts = {}; answers.forEach(arr => (arr || []).forEach(o => { counts[o] = (counts[o] || 0) + 1; }));
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'rating') {
      const nums = answers.filter(a => typeof a === 'number');
      const avg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      const counts = {}; nums.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
      return { type: 'rating', avg: avg.toFixed(1), max: q.ratingMax || 5, counts, total: nums.length };
    }
    if (q.type === 'number') {
      const nums = answers.map(Number).filter(n => !isNaN(n));
      return { type: 'number', avg: (nums.reduce((a, b) => a + b, 0) / nums.length || 0).toFixed(1), total: nums.length };
    }
    return { type: 'text', answers, total: answers.length };
  }

  let body = `
    <h1>${survey.title || 'Survey Results'}</h1>
    <p class="meta">${responses.length} response${responses.length !== 1 ? 's' : ''} &middot; Exported ${date}</p>
    <hr class="divider">`;

  for (const q of questions) {
    const stats = getStats(q);
    body += `<h3><span class="label">${q.type.replace(/_/g, ' ')}</span><br>${q.label || 'Untitled Question'}</h3>`;
    if (!stats) { body += `<p class="meta">No responses</p>`; continue; }
    if (stats.type === 'bar') {
      Object.entries(stats.counts).sort((a, b) => b[1] - a[1]).forEach(([opt, count]) => {
        const pct = Math.round(count / stats.total * 100);
        body += `<p class="opt-row">${opt} &nbsp;&mdash;&nbsp; ${count} (${pct}%)</p>`;
      });
      body += `<p class="meta">${stats.total} response${stats.total !== 1 ? 's' : ''}</p>`;
    } else if (stats.type === 'rating' || stats.type === 'number') {
      body += `<p><b>${stats.avg}</b>${stats.type === 'rating' ? ` / ${stats.max}` : ''} average &middot; ${stats.total} responses</p>`;
    } else if (stats.type === 'text') {
      stats.answers.forEach(a => { body += `<p class="answer">"${a}"</p>`; });
    }
    body += `<hr class="divider">`;
  }

  triggerDownload(wordBlob(body), `${survey.title || 'survey'}-summary.doc`);
}

// ── 2. Individual responses Word export (one block per respondent, in order) ──
function downloadIndividualWord(survey, responses) {
  const questions = (survey.questions || []).filter(q => q.type !== 'section');
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // responses are already ordered newest-first from Firestore; reverse so #1 is first submitted
  const ordered = [...responses].reverse();

  let body = `
    <h1>${survey.title || 'Survey'} — Individual Responses</h1>
    <p class="meta">${ordered.length} response${ordered.length !== 1 ? 's' : ''} &middot; Exported ${date}</p>
    <hr class="divider">`;

  ordered.forEach((resp, i) => {
    const submitted = resp.submittedAt?.toDate?.()?.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    }) || '—';
    body += `
      <div class="respondent-block">
        <h2>Response #${i + 1} — ${resp.respondentName || 'Anonymous'}</h2>
        <p class="meta">${resp.respondentEmail || 'No email'} &middot; ${submitted}</p>`;

    questions.forEach(q => {
      const ans = resp.answers?.[q.id];
      if (ans === undefined || ans === null || ans === '') return;
      body += `<p class="label" style="margin-top:8pt;">${q.label || 'Untitled Question'}</p>`;
      if (q.type === 'file_upload') {
        const files = Array.isArray(ans) ? ans : [ans];
        files.forEach(f => { body += `<p class="answer">📄 ${f.name || f.url}</p>`; });
      } else {
        body += `<p class="answer">${Array.isArray(ans) ? ans.join(', ') : String(ans)}</p>`;
      }
    });

    body += `</div><hr class="divider">`;
  });

  triggerDownload(wordBlob(body), `${survey.title || 'survey'}-responses.doc`);
}

// ─── PDF export ────────────────────────────────────────────────────────────────
function printPDF(survey, responses) {
  const questions = (survey.questions || []).filter(q => q.type !== 'section');
  const accentColor = survey.settings?.accentColor || '#1a2744';
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Compute stats per question (same logic as getStats)
  function getStats(q) {
    const answers = responses.map(r => r.answers[q.id]).filter(a => a !== undefined && a !== null && a !== '');
    if (answers.length === 0) return null;
    if (q.type === 'multiple_choice' || q.type === 'dropdown') {
      const counts = {};
      answers.forEach(a => { counts[a] = (counts[a] || 0) + 1; });
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'checkboxes') {
      const counts = {};
      answers.forEach(arr => { (arr || []).forEach(opt => { counts[opt] = (counts[opt] || 0) + 1; }); });
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'rating') {
      const nums = answers.filter(a => typeof a === 'number');
      const avg = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      const counts = {};
      nums.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
      return { type: 'rating', avg: avg.toFixed(1), max: q.ratingMax || 5, counts, total: nums.length };
    }
    if (q.type === 'number') {
      const nums = answers.map(Number).filter(n => !isNaN(n));
      const avg = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      return { type: 'number', avg: avg.toFixed(1), total: nums.length };
    }
    if (q.type === 'file_upload') {
      const files = answers.flatMap(a => Array.isArray(a) ? a : [a])
        .filter(f => f && (f.url || typeof f === 'string'));
      const normalized = files.map(f =>
        typeof f === 'string' ? { name: f.split('/').pop().split('?')[0], url: f } : f
      );
      return { type: 'files', files: normalized, total: answers.length };
    }
    return { type: 'text', answers, total: answers.length };
  }

  let questionsHTML = '';
  for (const q of questions) {
    const stats = getStats(q);
    let body = '<p style="font-size:12px;color:#94a3b8;margin:0;">No responses</p>';

    if (stats?.type === 'bar') {
      const sorted = Object.entries(stats.counts).sort((a, b) => b[1] - a[1]);
      body = sorted.map(([opt, count]) => {
        const pct = Math.round(count / stats.total * 100);
        return `
          <div style="margin-bottom:8px;">
            <div style="display:flex;justify-content:space-between;font-size:12px;color:#334155;margin-bottom:3px;">
              <span>${opt}</span><span style="color:#94a3b8;">${count} (${pct}%)</span>
            </div>
            <div style="height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden;">
              <div style="width:${pct}%;background:${accentColor};height:100%;border-radius:4px;"></div>
            </div>
          </div>`;
      }).join('') + `<p style="font-size:11px;color:#94a3b8;margin:8px 0 0;">${stats.total} response${stats.total !== 1 ? 's' : ''}</p>`;
    } else if (stats?.type === 'rating') {
      body = `
        <p style="font-size:36px;font-weight:700;color:${accentColor};text-align:center;margin:8px 0 0;">${stats.avg}</p>
        <p style="font-size:12px;color:#94a3b8;text-align:center;margin:0 0 12px;">out of ${stats.max} · ${stats.total} responses</p>
        ${Array.from({ length: stats.max }, (_, i) => i + 1).map(n => {
          const count = stats.counts[n] || 0;
          const pct = stats.total > 0 ? Math.round(count / stats.total * 100) : 0;
          return `<div style="margin-bottom:6px;">
            <div style="display:flex;justify-content:space-between;font-size:12px;color:#334155;margin-bottom:2px;"><span>${n}</span><span style="color:#94a3b8;">${count}</span></div>
            <div style="height:6px;background:#f1f5f9;border-radius:3px;overflow:hidden;"><div style="width:${pct}%;background:${accentColor};height:100%;"></div></div>
          </div>`;
        }).join('')}`;
    } else if (stats?.type === 'number') {
      body = `<p style="font-size:36px;font-weight:700;color:${accentColor};text-align:center;margin:8px 0 0;">${stats.avg}</p>
              <p style="font-size:12px;color:#94a3b8;text-align:center;margin:0;">average · ${stats.total} responses</p>`;
    } else if (stats?.type === 'text') {
      body = stats.answers.slice(0, 15).map(a =>
        `<div style="padding:6px 0;border-bottom:1px solid #f1f5f9;font-size:12px;color:#334155;line-height:1.5;">"${a}"</div>`
      ).join('') + (stats.answers.length > 15
        ? `<p style="font-size:11px;color:#94a3b8;margin:6px 0 0;">+${stats.answers.length - 15} more responses</p>` : '');
    } else if (stats?.type === 'files') {
      body = `<p style="font-size:12px;color:#94a3b8;margin:0 0 8px;">${stats.total} file${stats.total !== 1 ? 's' : ''} uploaded</p>` +
        stats.files.slice(0, 15).map(f =>
          `<div style="padding:5px 0;border-bottom:1px solid #f1f5f9;font-size:12px;color:#334155;">📄 <a href="${f.url}" style="color:${accentColor};">${f.name}</a></div>`
        ).join('') + (stats.files.length > 15
          ? `<p style="font-size:11px;color:#94a3b8;margin:6px 0 0;">+${stats.files.length - 15} more files</p>` : '');
    }

    questionsHTML += `
      <div style="border:1px solid #e2e8f0;border-radius:10px;padding:16px 20px;margin-bottom:14px;page-break-inside:avoid;">
        <p style="font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#f59e0b;margin:0 0 4px;">${q.type.replace('_', ' ')}</p>
        <p style="font-size:14px;font-weight:600;color:#1a2744;margin:0 0 12px;">${q.label || 'Untitled Question'}</p>
        ${body}
      </div>`;
  }

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${survey.title} — Results</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #1e293b; padding: 32px; font-size: 13px; }
    @media print { body { padding: 16px; } @page { margin: 1.2cm; size: A4; } }
  </style>
</head>
<body>
  <div style="border-bottom:3px solid ${accentColor};padding-bottom:16px;margin-bottom:24px;">
    <h1 style="font-size:22px;font-weight:700;color:${accentColor};margin-bottom:4px;">${survey.title}</h1>
    <p style="color:#64748b;font-size:12px;">${responses.length} response${responses.length !== 1 ? 's' : ''} &middot; Exported ${date}</p>
  </div>
  ${questionsHTML}
</body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 500);
}

export default function SurveyResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile, profileLoading } = useAuth();

  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary');
  const [copied, setCopied] = useState(false);
  const [statusToggling, setStatusToggling] = useState(false);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'surveys', id)),
      getDocs(query(collection(db, 'surveys', id, 'responses'), orderBy('submittedAt', 'desc'))),
    ]).then(([surveySnap, responsesSnap]) => {
      if (surveySnap.exists()) setSurvey(surveySnap.data());
      setResponses(responsesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [id]);

  const publicUrl = `${window.location.origin}/s/${id}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSurveyStatus = async () => {
    const newStatus = survey.status === 'closed' ? 'active' : 'closed';
    setStatusToggling(true);
    try {
      await updateDoc(doc(db, 'surveys', id), { status: newStatus });
      setSurvey(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Failed to toggle survey status:', err);
    }
    setStatusToggling(false);
  };

  // Compute summary stats for a question
  const getStats = (q) => {
    const answers = responses.map(r => r.answers[q.id]).filter(a => a !== undefined && a !== null && a !== '');
    if (answers.length === 0) return null;

    if (q.type === 'multiple_choice' || q.type === 'dropdown') {
      const counts = {};
      answers.forEach(a => { counts[a] = (counts[a] || 0) + 1; });
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'checkboxes') {
      const counts = {};
      answers.forEach(arr => {
        (arr || []).forEach(opt => { counts[opt] = (counts[opt] || 0) + 1; });
      });
      return { type: 'bar', counts, total: answers.length };
    }
    if (q.type === 'rating') {
      const nums = answers.filter(a => typeof a === 'number');
      const avg = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      const counts = {};
      nums.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
      return { type: 'rating', avg: avg.toFixed(1), max: q.ratingMax || 5, counts, total: nums.length };
    }
    if (q.type === 'number') {
      const nums = answers.map(Number).filter(n => !isNaN(n));
      const avg = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      return { type: 'number', avg: avg.toFixed(1), total: nums.length };
    }
    if (q.type === 'file_upload') {
      // Each answer is {name,url} or [{name,url},...] — normalize to flat array of file objects
      const files = answers.flatMap(a => Array.isArray(a) ? a : [a])
        .filter(f => f && (f.url || typeof f === 'string'));
      // Normalize plain strings (legacy) to {name, url}
      const normalized = files.map(f =>
        typeof f === 'string' ? { name: f.split('/').pop().split('?')[0], url: f } : f
      );
      return { type: 'files', files: normalized, total: answers.length }; // total = respondents, not files
    }
    return { type: 'text', answers, total: answers.length };
  };

  const accentColor = survey?.settings?.accentColor || '#1a2744';

  if (loading || profileLoading) return <div style={{ padding: 40, color: 'var(--ka-gray-400)' }}>Loading results…</div>;
  if (!survey) return <div style={{ padding: 40 }}>Survey not found.</div>;

  // Access check
  const accessRoles = survey.settings?.accessRoles || ['admin'];
  const isSuperAdmin = profile?.role === 'superadmin' || profile?.role === 'dean' || profile?.superadmin === true;
  const isOwner = survey.createdBy === user?.uid;
  const isSharedWithMe = (survey.sharedWith || []).includes(user?.uid);
  if (!isSuperAdmin && !isOwner && !isSharedWithMe && !accessRoles.includes(profile?.role)) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--ka-gray-400)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 8 }}>Access Restricted</div>
        <div>You don't have permission to view results for this survey.</div>
      </div>
    );
  }

  const canManage = isSuperAdmin || isOwner;

  const tabStyle = (t) => ({ ...s.tab, ...(tab === t ? s.tabActive : {}) });
  const statusColors = { active: { bg: '#d1fae5', color: '#065f46' }, draft: { bg: '#f3f4f6', color: '#6b7280' }, closed: { bg: '#fee2e2', color: '#991b1b' } };
  const sc = statusColors[survey.status] || statusColors.draft;

  const hasResponses = responses.length > 0;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.titleRow}>
          <button style={s.backBtn} onClick={() => navigate('/tools/surveys')}>← Surveys</button>
          <div style={s.title}>{survey.title}</div>
          <span style={{ ...s.statusPill, background: sc.bg, color: sc.color }}>{survey.status}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {hasResponses && (
              <>
                <button
                  style={{ ...s.backBtn, display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => downloadCSV(survey, responses)}
                  title="Download all responses as CSV"
                >
                  ⬇ CSV
                </button>
                <button
                  style={{ ...s.backBtn, display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => downloadWord(survey, responses)}
                  title="Download aggregated summary as Word doc"
                >
                  📄 Summary (.doc)
                </button>
                <button
                  style={{ ...s.backBtn, display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => downloadIndividualWord(survey, responses)}
                  title="Download each response individually with respondent name"
                >
                  👤 By Respondent (.doc)
                </button>
                <button
                  style={{ ...s.backBtn, display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => printPDF(survey, responses)}
                  title="Export summary as PDF"
                >
                  🖨 PDF
                </button>
              </>
            )}
            {canManage && (
              <button
                style={{
                  ...s.backBtn,
                  background: survey.status === 'closed' ? '#d1fae5' : '#fee2e2',
                  color: survey.status === 'closed' ? '#065f46' : '#991b1b',
                  borderColor: survey.status === 'closed' ? '#6ee7b7' : '#fca5a5',
                  opacity: statusToggling ? 0.6 : 1,
                  cursor: statusToggling ? 'not-allowed' : 'pointer',
                }}
                onClick={toggleSurveyStatus}
                disabled={statusToggling}
                title={survey.status === 'closed' ? 'Reopen this survey to accept responses' : 'Stop accepting new responses'}
              >
                {statusToggling
                  ? '…'
                  : survey.status === 'closed'
                    ? '🔓 Reopen Survey'
                    : '🔒 Close Survey'}
              </button>
            )}
            <button
              style={s.backBtn}
              onClick={() => navigate(`/tools/surveys/edit/${id}`)}
            >
              Edit Survey
            </button>
          </div>
        </div>
      </div>

      {/* Share bar */}
      {survey.status === 'active' && (
        <div style={s.shareBar}>
          <span style={{ fontSize: 16 }}>🔗</span>
          <span style={{ fontSize: 12, color: 'var(--ka-gray-600)', fontWeight: 600, whiteSpace: 'nowrap' }}>Share URL:</span>
          <span style={{
            flex: 1, fontSize: 12, fontFamily: 'monospace',
            color: 'var(--ka-navy)', background: 'var(--ka-gray-50)',
            padding: '6px 10px', borderRadius: 6,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {publicUrl}
          </span>
          <button style={s.copyBtn} onClick={copyUrl}>
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
          <button style={{ ...s.copyBtn, background: 'var(--ka-navy)', color: 'white' }} onClick={() => window.open(`/s/${id}`, '_blank')}>
            Preview
          </button>
        </div>
      )}

      {/* Stats */}
      <div style={s.statsRow}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Total Responses</div>
          <div style={s.statVal}>{responses.length}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Questions</div>
          <div style={s.statVal}>{(survey.questions || []).filter(q => q.type !== 'section').length}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Latest Response</div>
          <div style={{ ...s.statVal, fontSize: 16, paddingTop: 6 }}>
            {responses[0]?.submittedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '—'}
          </div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Status</div>
          <div style={{ ...s.statVal, fontSize: 18, paddingTop: 6, color: sc.color, textTransform: 'capitalize' }}>
            {survey.status}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        <button style={tabStyle('summary')} onClick={() => setTab('summary')}>📊 Summary</button>
        <button style={tabStyle('responses')} onClick={() => setTab('responses')}>📋 Individual Responses</button>
      </div>

      {/* Summary tab */}
      {tab === 'summary' && (
        responses.length === 0 ? (
          <div style={s.noData}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 8 }}>No responses yet</div>
            <div>Share the survey link to start collecting responses.</div>
          </div>
        ) : (
          <div style={s.summaryGrid}>
            {(survey.questions || []).filter(q => q.type !== 'section').map(q => {
              const stats = getStats(q);
              const isWide = q.type === 'short_text' || q.type === 'long_text' || q.type === 'file_upload';
              return (
                <div key={q.id} style={{ ...s.qCard, ...(isWide ? s.qCardFull : {}) }}>
                  <div style={s.qType}>{q.type.replace('_', ' ')}</div>
                  <div style={s.qLabel}>{q.label || 'Untitled Question'}</div>

                  {!stats && <div style={{ color: 'var(--ka-gray-400)', fontSize: 13 }}>No responses yet</div>}

                  {stats?.type === 'bar' && (
                    <div>
                      {Object.entries(stats.counts).sort((a, b) => b[1] - a[1]).map(([opt, count]) => (
                        <div key={opt} style={s.barRow}>
                          <div style={s.barLabel}>
                            <span>{opt}</span>
                            <span style={{ color: 'var(--ka-gray-400)' }}>{count} ({Math.round(count / stats.total * 100)}%)</span>
                          </div>
                          <div style={s.barOuter}>
                            <div style={{ ...s.barFill, width: `${count / stats.total * 100}%`, background: accentColor }} />
                          </div>
                        </div>
                      ))}
                      <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', marginTop: 8 }}>{stats.total} response{stats.total !== 1 ? 's' : ''}</div>
                    </div>
                  )}

                  {stats?.type === 'rating' && (
                    <div>
                      <div style={s.ratingAvg} >{stats.avg}</div>
                      <div style={s.ratingMax}>out of {stats.max} · {stats.total} response{stats.total !== 1 ? 's' : ''}</div>
                      <div style={{ marginTop: 16 }}>
                        {Array.from({ length: stats.max }, (_, i) => i + 1).map(n => {
                          const count = stats.counts[n] || 0;
                          return (
                            <div key={n} style={s.barRow}>
                              <div style={s.barLabel}>
                                <span>{n}</span>
                                <span style={{ color: 'var(--ka-gray-400)' }}>{count}</span>
                              </div>
                              <div style={s.barOuter}>
                                <div style={{ ...s.barFill, width: `${stats.total > 0 ? count / stats.total * 100 : 0}%`, background: accentColor }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {stats?.type === 'number' && (
                    <div>
                      <div style={s.ratingAvg}>{stats.avg}</div>
                      <div style={s.ratingMax}>average · {stats.total} response{stats.total !== 1 ? 's' : ''}</div>
                    </div>
                  )}

                  {stats?.type === 'text' && (
                    <div style={s.textResponses}>
                      {stats.answers.slice(0, 20).map((a, i) => (
                        <div key={i} style={s.textItem}>"{a}"</div>
                      ))}
                      {stats.answers.length > 20 && (
                        <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', padding: '8px 0' }}>
                          +{stats.answers.length - 20} more (view in Individual Responses)
                        </div>
                      )}
                    </div>
                  )}

                  {stats?.type === 'files' && (
                    <div style={s.textResponses}>
                      <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', marginBottom: 8 }}>
                        {stats.total} respondent{stats.total !== 1 ? 's' : ''} uploaded {stats.files.length} file{stats.files.length !== 1 ? 's' : ''}
                      </div>
                      {stats.files.slice(0, 20).map((f, i) => (
                        <div key={i} style={{ ...s.textItem, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16 }}>📄</span>
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: accentColor, textDecoration: 'underline', fontSize: 13, wordBreak: 'break-all' }}
                          >
                            {f.name}
                          </a>
                        </div>
                      ))}
                      {stats.files.length > 20 && (
                        <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', padding: '8px 0' }}>
                          +{stats.files.length - 20} more (view in Individual Responses)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Individual responses tab */}
      {tab === 'responses' && (
        responses.length === 0 ? (
          <div style={s.noData}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 8 }}>No responses yet</div>
          </div>
        ) : (
          <div>
            {responses.map((resp, i) => (
              <div key={resp.id} style={s.responseCard}>
                <div style={s.responseHead}>
                  <span style={{ fontWeight: 700, color: 'var(--ka-navy)' }}>
                    {resp.respondentName || 'Anonymous'}
                  </span>
                  {resp.respondentEmail && (
                    <span style={{ color: 'var(--ka-gray-400)' }}>{resp.respondentEmail}</span>
                  )}
                  <span style={{ marginLeft: 'auto', color: 'var(--ka-gray-400)' }}>
                    {resp.submittedAt?.toDate?.()?.toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    }) || '—'}
                  </span>
                  <span style={{ color: 'var(--ka-gray-400)' }}>#{responses.length - i}</span>
                </div>
                <div style={s.responseBody}>
                  {(survey.questions || []).filter(q => q.type !== 'section').map(q => {
                    const ans = resp.answers[q.id];
                    if (ans === undefined || ans === null || ans === '') return null;
                    return (
                      <div key={q.id} style={s.responseItem}>
                        <div style={s.responseQ}>{q.label || 'Untitled Question'}</div>
                        {q.type === 'file_upload' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {(Array.isArray(ans) ? ans : [ans]).map((f, fi) => (
                              <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 15 }}>📄</span>
                                <a
                                  href={f.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: accentColor, textDecoration: 'underline', fontSize: 14, wordBreak: 'break-all' }}
                                >
                                  {f.name}
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={s.responseA}>
                            {Array.isArray(ans) ? ans.join(', ') : String(ans)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}